import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useMessage } from '../../contexts/MessageContext';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const ApplicationsTab = () => {
  const navigate = useNavigate();
  const [companyLegalName, setCompanyLegalName] = useState('');
  const [companySaving, setCompanySaving] = useState(false);

  const [logoUrl, setLogoUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [logoSaving, setLogoSaving] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [contractsEnabled, setContractsEnabled] = useState(false);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  const [featureSaving, setFeatureSaving] = useState(false);
  const { refreshFeatures } = useFeatures();
  const { showSuccess, showError } = useMessage();

  const [productS3Folder, setProductS3Folder] = useState('rentals');
  const [defaultProductImageUrl, setDefaultProductImageUrl] = useState('');
  const [productsS3Bucket, setProductsS3Bucket] = useState('');
  const [catalogAssetsLoaded, setCatalogAssetsLoaded] = useState(false);
  const [catalogAssetsSaving, setCatalogAssetsSaving] = useState(false);

  const loadBranding = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/settings/branding`);
      if (res.data.success) {
        if (res.data.company_legal_name) {
          setCompanyLegalName(res.data.company_legal_name);
        }
        if (res.data.logo_url) {
          setLogoUrl(res.data.logo_url);
          setLogoPreview(res.data.logo_url);
        }
      }
    } catch {
      try {
        const res = await axios.get(`${apiUrl}/settings/logo`);
        if (res.data.logo_url) {
          setLogoUrl(res.data.logo_url);
          setLogoPreview(res.data.logo_url);
        }
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { 'x-auth-token': token };

    const load = async () => {
      try {
        const [profRes, featRes] = await Promise.all([
          axios.get(`${apiUrl}/user/profile`, { headers }),
          axios.get(`${apiUrl}/settings/features`, { headers }),
        ]);
        const role = String(profRes.data.user?.role || '').toLowerCase();
        setUserRole(profRes.data.user?.role || '—');
        setIsAdmin(role === 'admin');
        const fromProfile = profRes.data.features?.contracts;
        const fromSettings = featRes.data.features?.contracts;
        setContractsEnabled(Boolean(fromProfile ?? fromSettings));
      } catch {
        /* unknown */
      } finally {
        setFeaturesLoaded(true);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { 'x-auth-token': token };
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/settings/catalog-assets`, { headers });
        if (res.data.success) {
          setProductS3Folder(res.data.product_s3_folder || 'rentals');
          setDefaultProductImageUrl(res.data.default_product_image_url || '');
          setProductsS3Bucket(res.data.products_s3_bucket || '');
        }
      } catch {
        /* ignore */
      } finally {
        setCatalogAssetsLoaded(true);
      }
    })();
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveCompanyName = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please sign in to save settings.');
      return;
    }
    setCompanySaving(true);
    try {
      await axios.patch(
        `${apiUrl}/settings/branding`,
        { company_legal_name: companyLegalName },
        { headers: { 'x-auth-token': token } }
      );
      await loadBranding();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save company name');
    } finally {
      setCompanySaving(false);
    }
  };

  const handleLogoSave = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Please sign in to save settings.');
      return;
    }
    setLogoSaving(true);
    try {
      await axios.post(
        `${apiUrl}/settings/logo`,
        { logo_url: logoPreview },
        { headers: { 'x-auth-token': token } }
      );
      setLogoUrl(logoPreview);
      await loadBranding();
      showSuccess('Logo saved.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save logo');
    } finally {
      setLogoSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50 px-5 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Company branding</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Legal or display name and logo are read from the database for quotes, invoices, and rental contracts (HTML /
            PDF).
          </p>
        </div>
        <div className="p-5 space-y-6">
          <div>
            <label htmlFor="company-legal-name" className="block text-sm font-medium text-gray-700 mb-1">
              Company / legal name
            </label>
            <input
              id="company-legal-name"
              type="text"
              value={companyLegalName}
              onChange={(e) => setCompanyLegalName(e.target.value)}
              placeholder="e.g. Acme Event Rentals LLC"
              maxLength={250}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Shown as &quot;Prepared by …&quot; on contracts and in the header area on public quote and invoice pages.
              If empty, the server falls back to environment configuration then a default label.
            </p>
            <button
              type="button"
              onClick={handleSaveCompanyName}
              disabled={companySaving || !companyLegalName.trim()}
              className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {companySaving ? 'Saving…' : 'Save company name'}
            </button>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
            <p className="text-xs text-gray-500 mb-3">
              Used on public quotes, invoices, and contract documents. Upload a PNG or JPG (wide logos work best).
            </p>
            <input type="file" accept="image/*" onChange={handleLogoChange} className="block text-sm text-gray-600" />
            {logoPreview && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100 inline-block">
                <img src={logoPreview} alt="Logo preview" className="h-24 max-w-xs object-contain" />
              </div>
            )}
            {logoUrl && logoPreview && logoUrl !== logoPreview && (
              <p className="mt-2 text-xs text-amber-700">Unsaved logo selection. Click Save logo to apply.</p>
            )}
            <button
              type="button"
              onClick={handleLogoSave}
              disabled={logoSaving || !logoPreview}
              className="mt-4 block px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900 disabled:opacity-50"
            >
              {logoSaving ? 'Saving…' : 'Save logo'}
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50 px-5 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Catalog product images</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Staff uploads on Add / Edit inventory go to your <strong>product</strong> bucket (default{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">mler-products</code>
            ). This is separate from the <strong>site gallery</strong> under{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">gallery/</code>. Set{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">PRODUCTS_S3_BUCKET</code> on the API server if needed.
          </p>
        </div>
        <div className="p-5 space-y-5">
          {productsS3Bucket && (
            <p className="text-xs text-gray-600">
              Configured bucket:{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">{productsS3Bucket}</code>
            </p>
          )}
          {!catalogAssetsLoaded ? (
            <p className="text-sm text-gray-500">Loading catalog settings…</p>
          ) : (
            <>
              <div>
                <label htmlFor="product-s3-folder" className="block text-sm font-medium text-gray-700 mb-1">
                  Product upload folder
                </label>
                <input
                  id="product-s3-folder"
                  type="text"
                  value={productS3Folder}
                  onChange={(e) => setProductS3Folder(e.target.value)}
                  placeholder="e.g. rentals, chairs, storefront"
                  className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Used as <code className="text-xs">products/&lt;this-folder&gt;/</code> — letters, numbers, hyphens only
                  (unsafe characters are stripped server-side).
                </p>
              </div>
              <div>
                <label htmlFor="default-product-image" className="block text-sm font-medium text-gray-700 mb-1">
                  Default product image URL (website)
                </label>
                <input
                  id="default-product-image"
                  type="url"
                  value={defaultProductImageUrl}
                  onChange={(e) => setDefaultProductImageUrl(e.target.value)}
                  placeholder="https://… or leave blank"
                  className="w-full max-w-xl border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Shown on the public storefront when a product has no photos. Returned from{' '}
                  <code className="text-xs">GET /api/website</code> as{' '}
                  <code className="text-xs">default_product_image_url</code>. The staff app bundles a stencil at{' '}
                  <code className="text-xs">/default-product-placeholder.svg</code> you can mirror on the site CDN.
                </p>
              </div>
              <button
                type="button"
                disabled={catalogAssetsSaving}
                onClick={async () => {
                  const token = localStorage.getItem('token');
                  if (!token) {
                    alert('Please sign in to save.');
                    return;
                  }
                  setCatalogAssetsSaving(true);
                  try {
                    const res = await axios.patch(
                      `${apiUrl}/settings/catalog-assets`,
                      {
                        product_s3_folder: productS3Folder.trim() || 'rentals',
                        default_product_image_url: defaultProductImageUrl.trim()
                          ? defaultProductImageUrl.trim()
                          : null,
                      },
                      { headers: { 'x-auth-token': token } }
                    );
                    if (res.data?.success) {
                      setProductS3Folder(res.data.product_s3_folder || 'rentals');
                      setDefaultProductImageUrl(res.data.default_product_image_url || '');
                      setProductsS3Bucket(res.data.products_s3_bucket || '');
                      showSuccess('Catalog settings saved.');
                    } else {
                      showError(res.data?.message || 'Could not save catalog settings.');
                    }
                  } catch (err) {
                    const msg = err.response?.data?.message || err.message || 'Failed to save catalog settings';
                    showError(msg);
                  } finally {
                    setCatalogAssetsSaving(false);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {catalogAssetsSaving ? 'Saving…' : 'Save catalog settings'}
              </button>
            </>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50 px-5 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Site gallery</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Public storefront gallery images are stored in your bucket under the{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">gallery/</code> prefix. Open the manager to see folders,
            preview images, and upload new files.
          </p>
        </div>
        <div className="p-5">
          <button
            type="button"
            onClick={() => navigate('/settings/gallery')}
            className="px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
          >
            Manage gallery
          </button>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50 px-5 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Product add-ons</h2>
          <p className="text-sm text-gray-600 mt-0.5">Optional modules stored on the server.</p>
        </div>
        <div className="p-5">
          <h3 className="font-medium text-gray-900 mb-1">Contracts</h3>
          <p className="text-sm text-gray-600 mb-4">
            Generate rental contracts from quotes (HTML + PDF) and share a customer acceptance link. Can also be toggled
            via env <code className="text-xs bg-gray-100 px-1 rounded">FEATURE_CONTRACTS</code> or{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">product_features</code> in the database.
          </p>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-sm font-medium text-gray-700">Status</span>
            {!featuresLoaded ? (
              <span className="text-sm text-gray-500">Loading…</span>
            ) : (
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                  contractsEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {contractsEnabled ? 'Enabled' : 'Disabled'}
              </span>
            )}
          </div>
          {isAdmin ? (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={contractsEnabled}
                disabled={featureSaving || !featuresLoaded}
                onChange={async () => {
                  const next = !contractsEnabled;
                  setFeatureSaving(true);
                  try {
                    await axios.patch(
                      `${apiUrl}/settings/product-features`,
                      { contracts: next },
                      { headers: { 'x-auth-token': localStorage.getItem('token') } }
                    );
                    setContractsEnabled(next);
                    await refreshFeatures();
                  } catch {
                    alert('Could not update product features.');
                  } finally {
                    setFeatureSaving(false);
                  }
                }}
              />
              <span className="text-sm text-gray-800">Enable contracts add-on</span>
            </label>
          ) : (
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Only <strong>admin</strong> users can change this toggle. Your role is{' '}
              <strong>{userRole || 'unknown'}</strong>.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default ApplicationsTab;
